import { SlotFilter, WorkingHourRange } from '../../models/slot-calendar.model';
import { sortByDateAsc } from '../../utils/general.utils';
import { splitWith } from '../../utils/string.utils';
import { Slot } from '../slots/slots.types';
import { FieldUpdate, SlotCalendarFilter, UploadSlotDocumentDto } from './slot-calendar.types';

export const toFieldUpdates = <T extends object>(payload: Partial<T>): Array<FieldUpdate<T>> => {
  const out: Array<FieldUpdate<T>> = [];
  for (const k in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, k)) {
      const key = k as keyof T;
      const value = payload[key];
      if (value !== undefined) {
        out.push({ key, value } as FieldUpdate<T>);
      }
    }
  }
  return out;
};

const toArray = <T>(v: T | T[]): T[] => {
  return Array.isArray(v) ? v : [v];
};

export const buildSlotDocumentFormData = (dto: UploadSlotDocumentDto): FormData => {
  const fd = new FormData();

  for (const file of toArray(dto.content)) {
    fd.append('Content', file);
  }

  fd.append('Types', dto.types);
  return fd;
};

/**
 * Normalizes working-hour ranges:
 * - Keys (dock identifiers) are uppercased.
 * - All `dateFrom` and `dateTo` values have any trailing `"Z"` removed.
 *
 * This is typically used to normalize API data before it is consumed by the
 * compact calendar or other time-based UI.
 *
 * @param data Map of dock ID to an array of working hour ranges.
 * @returns A new map with uppercased keys and UTC suffixes stripped.
 */
export function normalizeWorkingHours(
  data: Record<string, WorkingHourRange[]>,
): Record<string, WorkingHourRange[]> {
  return Object.entries(data).reduce(
    (
      acc: Record<string, WorkingHourRange[]>,
      entry: [string, WorkingHourRange[]],
    ): Record<string, WorkingHourRange[]> => {
      const key = entry[0];
      const periods = entry[1];

      acc[key.toUpperCase()] = periods.map((period: WorkingHourRange): WorkingHourRange => {
        return {
          dateFrom: stripUtcSuffix(period.dateFrom) as string,
          dateTo: stripUtcSuffix(period.dateTo) as string,
        };
      });

      return acc;
    },
    {} as Record<string, WorkingHourRange[]>,
  );
}

/**
 * Removes a trailing `"Z"` (UTC designator) from an ISO datetime string.
 *
 * - If `value` is `null` or `undefined`, it is returned unchanged.
 * - If `value` does not end with `"Z"`, it is returned unchanged.
 *
 * @param value ISO datetime string, e.g. `"2025-01-01T10:00:00Z"`.
 * @returns The same string without a trailing `"Z"`, or the original `null`/`undefined`.
 */
function stripUtcSuffix(value: null | string | undefined): null | string | undefined {
  return !value ? value : value.replace(/Z$/, '');
}

/**
 * Normalize a filter value into a trimmed string.
 * Returns an empty string for null/undefined.
 * @param value - Value to normalize (e.g. from user input or API payload).
 * @returns Trimmed string representation, or '' when value is null/undefined.
 */
const normalizeText = (value: null | number | string | undefined): string =>
  value === null || value === undefined ? '' : String(value).trim();

/**
 * Case-insensitive substring check.
 * @param source - Source text to search in.
 * @param search - Text to search for. If empty, returns false.
 * @returns True if `search` is contained in `source`, ignoring case.
 */
const includesIgnoreCase = (source: string | undefined, search: string): boolean => {
  if (!search) return false;
  return (source || '').toLowerCase().includes(search.toLowerCase());
};

/**
 * Ensures all slots have a consistent `isFiltered` flag.
 * @param slots - Slots to normalize.
 * @param filter - Optional filter context used to compute `isFiltered` when missing.
 * @returns New slots array with normalized `isFiltered` values.
 */
export const normalizeSlots = (slots: Slot[], filter?: SlotFilter): Slot[] => {
  const filterCode = normalizeText(filter ? filter.slotCode : undefined);
  const hasFilterContext = !!filterCode;

  return slots.map((slot: Slot): Slot => {
    const isFiltered =
      slot.isFiltered !== undefined
        ? slot.isFiltered
        : hasFilterContext && includesIgnoreCase(slot.code, filterCode);

    return {
      ...slot,
      isFiltered: !!isFiltered,
    };
  });
};

/**
 * Returns the earliest (by `dateFrom`) slot that is marked as filtered.
 * @returns The first slot (earliest by `dateFrom`) with `isFiltered === true`, or `undefined` if none exists.
 */
export const getFirstFiltered = (slots: Slot[]): Slot | undefined =>
  slots
    .sort(sortByDateAsc((x: Slot): string => x.dateFrom))
    .find(({ isFiltered }): boolean => !!isFiltered);

/**
 * Normalizes a raw `SlotFilter` into a `SlotCalendarFilter` suitable for calendar downloading/filtering.
 * @returns A normalized calendar filter
 */
export const prepareSlotsFilter = (filter: SlotFilter): SlotCalendarFilter => ({
  docks: splitWith(filter.docks),
  shipmentNumbers: splitWith(filter.shipmentNumber),
  slotCodes: splitWith(filter.slotCode),
  slotDateFrom: filter.slotDateFrom,
  slotDateTo: filter.slotDateTo,
  storages: splitWith(filter.storages),
});
